import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import setAuthorizationToken from "utils/setAuthorizationToken";
import deleteAuthorizationToken from "utils/deleteAuthorizationToken";
import { getProfile, removeProfileData } from "store/profile";
import { getIsNewMessage } from "store/message/newMessage";
import { removeCompanyData } from "store/company";
import { socket } from "helpers/socket";
import { setCookie, deleteCookie } from "utils/cookies";

import { AUTH } from "endpointsConst";

const initialState = {
    authenticated: false,
    error: null,
};

let signInCount = 0;

export const signIn = createAsyncThunk(
    "auth/signIn",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const res = await axios.post(AUTH.SIGN_IN_URL, data);
            setCookie("access_token", res.data.accessToken, res.data.accessTokenExpiresIn);
            setCookie("refresh_token", res.data.refreshToken, res.data.refreshTokenExpiresIn);
            setAuthorizationToken();

            await dispatch(getProfile());
            dispatch(getIsNewMessage());

            return res.data;
        } catch (error) {
            if (error.response && error.response.status === 409 && signInCount === 0) {
                signInCount++;
                await dispatch(signOut());
                dispatch(signIn(data));
            }
            return rejectWithValue(error.response?.status || 500);
        }
    },
);

export const signUp = createAsyncThunk(
    "auth/signUp",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const res = await axios.post(AUTH.SIGN_UP_URL, data);
            setCookie("access_token", res.data.accessToken, res.data.accessTokenExpiresIn);
            setCookie("refresh_token", res.data.refreshToken, res.data.refreshTokenExpiresIn);
            setAuthorizationToken();

            await dispatch(getProfile());

            return res.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { errors: [] });
        }
    },
);

export const passwordRestore = createAsyncThunk(
    "auth/restore",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const res = await axios.put(AUTH.RESTORE, data);
            setCookie("access_token", res.data.accessToken, res.data.accessTokenExpiresIn);
            setCookie("refresh_token", res.data.refreshToken, res.data.refreshTokenExpiresIn);
            setAuthorizationToken();

            await dispatch(getProfile());
            return res.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { errors: [] });
        }
    },
);

export const signOut = createAsyncThunk("auth/signOut", async (data, { dispatch }) => {
    try {
        await axios.post(AUTH.LOGOUT);
    } catch (error) {
        console.error('Logout error:', error);
    }

    await dispatch(removeProfileData());
    await dispatch(removeCompanyData());
    return true;
});

const loginUser = (state, action) => {
    state.error = null;
    state.authenticated = true;
};

const setError = (state, action) => {
    state.error = {
        status: action.payload?.status || action.payload,
        errors: action.payload?.details || action.payload?.errors || [],
        message: action.payload?.message || action.payload?.title || 'Error',
    };
};

export const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        signInWithToken: (state, action) => {
            setCookie("access_token", action.payload.accessToken, action.payload.accessTokenExpiresIn);
            setCookie("refresh_token", action.payload.refreshToken, action.payload.refreshTokenExpiresIn);
            setAuthorizationToken();
            state.error = null;
            state.authenticated = true;
        },
        removeErrors: (state) => {
            state.error = null;
        },
    },
    extraReducers: {
        [signIn.fulfilled]: loginUser,
        [signIn.rejected]: setError,
        [signUp.fulfilled]: loginUser,
        [signUp.rejected]: setError,
        [passwordRestore.fulfilled]: loginUser,
        [passwordRestore.rejected]: setError,
        [signOut.fulfilled]: (state) => {
            deleteAuthorizationToken();
            deleteCookie("access_token");
            deleteCookie("refresh_token");
            state.authenticated = false;
            state.error = null;
            socket.disconnect();
        },
    },
});

export const { signInWithToken, removeErrors } = authSlice.actions;

export default authSlice.reducer;
